import React, { useState, useEffect, useRef } from 'react';
import { Bot, PaintBucket, FileText, ShoppingCart, Clock, Palette, Globe, BarChart3, Shield } from 'lucide-react';
import { useTheme } from './theme-provider';
import CalendlyModal from '../../components/CalendlyModal';

const features = [
  {
    icon: <Bot size={28} className="text-purple-500 dark:text-purple-400" />,
    title: "AI-Powered Chatbot Creation",
    description: "Build custom AI assistants that understand natural language and learn from your business data.",
    details: "Our advanced AI technology enables chatbots that can understand context, maintain conversation history, and provide accurate responses to complex queries based on your specific business knowledge."
  },
  {
    icon: <PaintBucket size={28} className="text-blue-500 dark:text-blue-400" />,
    title: "Easy Customization",
    description: "Customize your chatbot's appearance, behavior, and personality with our intuitive visual builder.",
    details: "Tailor your chatbot's look and feel to match your brand identity. Adjust fonts, colors, icons, and animations without any coding. Create custom conversation flows with our user-friendly drag-and-drop interface."
  },
  {
    icon: <FileText size={28} className="text-emerald-500 dark:text-emerald-400" />,
    title: "Knowledge Base Integration",
    description: "Train your bot on your documents, PDFs, and content so it can answer specific questions.",
    details: "Upload product manuals, FAQs, support documentation, and any other text-based content. Our system automatically processes and indexes this information, making it available for your chatbot to reference when answering customer queries."
  },
  {
    icon: <ShoppingCart size={28} className="text-amber-500 dark:text-amber-400" />,
    title: "E-commerce Integration",
    description: "Connect your online store to provide order tracking and product information automatically.",
    details: "Seamlessly integrate with WooCommerce, Shopify, and other major e-commerce platforms. Let your chatbot handle order status inquiries, product recommendations, inventory checks, and purchase assistance."
  },
  {
    icon: <Clock size={28} className="text-indigo-500 dark:text-indigo-400" />,
    title: "Behavioral Controls",
    description: "Set response delays and auto-open options for a more natural conversation experience.",
    details: "Configure typing indicators and response timing to simulate human-like conversation pacing. Set up triggers for when your chatbot should initiate conversation based on user behavior, page views, or time spent on site."
  },
  {
    icon: <Palette size={28} className="text-rose-500 dark:text-rose-400" />,
    title: "Appearance Customization",
    description: "Match your brand with custom colors, styling, and positioning options.",
    details: "Choose from various chat window layouts, customize button styles, and select placement options (corner, side, center). Apply your brand colors, logos, and typography for a consistent user experience."
  },
  {
    icon: <Globe size={28} className="text-teal-500 dark:text-teal-400" />,
    title: "Multi-platform Support",
    description: "Easily embed your chatbot on any website with a simple copy-paste integration.",
    details: "Deploy your chatbot across websites, mobile apps, and social media platforms with a single configuration. Our responsive design ensures optimal performance on any device or screen size."
  },
  {
    icon: <BarChart3 size={28} className="text-violet-500 dark:text-violet-400" />,
    title: "Analytics & Insights",
    description: "Track conversations and gather insights to improve your customer engagement.",
    details: "Access comprehensive analytics dashboards showing conversation metrics, user satisfaction ratings, common questions, and conversion rates. Use these insights to continuously refine your chatbot's knowledge and responses."
  },
  {
    icon: <Shield size={28} className="text-red-500 dark:text-red-400" />,
    title: "Security & Privacy",
    description: "Keep your data secure with encryption and robust privacy measures.",
    details: "Review our Privacy Policy for how information is collected, processed, retained, and shared. Contact us before purchasing if your deployment has specific regulatory or security requirements."
  }
];

const Features = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const featuresRef = useRef(null);
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  useEffect(() => {
    const observerOptions = { threshold: 0.1 };
    
    const createObserver = (ref, callback) => {
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          callback(true);
        }
      }, observerOptions);

      if (ref.current) {
        observer.observe(ref.current);
      }

      return () => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      };
    };

    // Set up observers for both sections
    const featuresCleanup = createObserver(featuresRef, () => setIsVisible(true));
    
    return () => {
      featuresCleanup();
    };
  }, []);

  return (
    <section 
      id="features" 
      className={`py-24 relative
        ${isDarkMode 
          ? 'bg-gradient-to-b from-gray-900 to-gray-950' 
          : 'bg-gradient-to-b from-white to-purple-50'
        }`}
    >
      <div className={`absolute inset-0 ${isDarkMode ? 'bg-grid-slate-800 opacity-20' : 'bg-grid-slate-100 opacity-25'} [mask-image:linear-gradient(to_bottom,transparent,black,transparent)]`}></div>
      
      {/* Dark mode specific glow effects */}
      {isDarkMode && (
        <>
          <div className="absolute top-1/4 left-1/4 w-3/4 h-72 bg-purple-600/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/3 w-1/2 h-72 bg-blue-600/10 rounded-full blur-3xl"></div>
        </>
      )}
      
      <div ref={featuresRef} className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-10">
          <h2 className={`text-4xl md:text-5xl font-bold ${isDarkMode ? 'text-white' : 'text-black'}`}>
            Powerful Features
          </h2>
          <p className={`mt-4 text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} max-w-2xl mx-auto`}>
            Everything you need to create intelligent, interactive AI chatbots
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Feature cards grid */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`relative overflow-hidden rounded-xl cursor-pointer transition-all duration-300 ${
                  isVisible ? 'animate-fade-in' : 'opacity-0'
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => setActiveFeature(index)}
              >
                <div className={`h-full p-5 flex flex-col justify-between ${
                  activeFeature === index 
                    ? isDarkMode
                      ? 'bg-gradient-to-br from-purple-900/90 to-indigo-900 text-white shadow-lg shadow-purple-900/20'
                      : 'bg-gradient-to-br from-black to-gray-700 text-white shadow-lg'
                    : isDarkMode
                      ? 'bg-gray-800/50 border border-gray-700 backdrop-blur-sm hover:border-purple-800 hover:bg-gray-800/70 hover:shadow-md hover:shadow-purple-900/10'
                      : 'bg-white border border-gray-200 hover:border-purple-200 hover:shadow-md'
                }`}>
                  <div className={`mb-3 ${activeFeature === index && !isDarkMode ? 'text-white' : ''}`}>
                    {feature.icon}
                  </div>
                  <h3 className={`font-semibold text-lg mb-2 ${
                    activeFeature === index 
                      ? 'text-white' 
                      : isDarkMode ? 'text-gray-100' : 'text-gray-800'
                  }`}>
                    {feature.title}
                  </h3>
                  <p className={`text-sm ${
                    activeFeature === index 
                      ? 'text-white/90' 
                      : isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Feature detail */}
          <div className={`lg:col-span-5 ${
            isDarkMode 
              ? 'bg-gray-800/60 border border-gray-700 backdrop-blur-sm shadow-xl shadow-purple-900/10' 
              : 'bg-white border border-gray-100 shadow-xl'
            } p-8 rounded-2xl lg:sticky lg:top-24`}>
            <div className="mb-6">
              <div className={`p-4 ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-purple-900/30 to-blue-900/30' 
                  : 'bg-gradient-to-br from-purple-50 to-blue-50'
                } rounded-xl mb-4 inline-block`}>
                {features[activeFeature].icon}
              </div>
              <h3 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {features[activeFeature].title}
              </h3>
              <div className={`h-1 w-20 ${
                isDarkMode 
                  ? 'bg-gradient-to-r from-purple-500 to-blue-500' 
                  : 'bg-gradient-to-r from-black to-gray-700'
                } rounded-full mb-6`}></div>
            </div>
            
            <div className="space-y-4">
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                {features[activeFeature].details}
              </p>
              
              <div className="pt-4">
                <button className={`${
                  isDarkMode ? 'text-purple-400 hover:text-purple-300' : 'text-black hover:text-gray-700'
                  } font-medium flex items-center transition-colors`}>
                  Learn more about this feature
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className={`mt-8 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} pt-6`}>
              <div className="flex justify-between items-center">
                <button 
                  onClick={() => setActiveFeature(prev => (prev === 0 ? features.length - 1 : prev - 1))}
                  className={`p-2 rounded-full ${
                    isDarkMode 
                      ? 'hover:bg-gray-700' 
                      : 'hover:bg-gray-100'
                    } transition-colors`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isDarkMode ? 'text-gray-300' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {activeFeature + 1} of {features.length}
                </span>
                
                <button 
                  onClick={() => setActiveFeature(prev => (prev === features.length - 1 ? 0 : prev + 1))}
                  className={`p-2 rounded-full ${
                    isDarkMode 
                      ? 'hover:bg-gray-700' 
                      : 'hover:bg-gray-100'
                    } transition-colors`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isDarkMode ? 'text-gray-300' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Calendly Modal */}
      <CalendlyModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default Features;
